import React from 'react';
import axios from 'axios';
import { isEmpty, isNil, noop } from 'lodash';

import { ASYNC_EXCHANGE_STATUS } from '~/components/AiChat/constants/asyncChat';
import { useChatSource } from '~/components/AiChat/contexts/ChatSourceContext';
import { ChatControllerProvider } from '~/components/AiChat/controller/ChatControllerContext';
import { CLAIM_CHAT_PRESENTATION } from '~/components/AiChat/controller/texts';
import type {
  ChatActiveExchange,
  ChatController,
  ChatExchangeView,
  ChatHistory,
  ChatRealtime,
  ClaimChatDocument,
  ClaimChatFeatures,
} from '~/components/AiChat/controller/types';
import { useChatContext } from '~/components/AiChat/hooks/useChat';
import type { OrganizedChatUpdates } from '~/components/AiChat/hooks/useChatRealTimeUpdates';
import type { PredefinedPromptItem } from '~/components/AiChat/hooks/usePredefinedPrompts';
import type { AiChatExchangeApiResponse, AiChatReferences } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import type { ClaimChatHistoryResponse } from '~/server_shared/openapi/data-contracts';

// Stable empties, so a claim without them does not hand the memoized chat components a new value per render.
const NO_DOCUMENT_IDS: number[] = [];
const NO_REFERENCE_IDS: string[] = [];
const NO_DOCUMENTS: ClaimChatDocument[] = [];
const NO_REFERENCES: AiChatReferences = {};
const NO_PROMPTS: Record<string, PredefinedPromptItem> = {};

export const resolveAgentEvents = (
  isLast: boolean,
  exchangeId: string,
  organizedRealTimeUpdates: OrganizedChatUpdates,
  isFetchingResponse: boolean,
  isFailed: boolean
) => {
  if (isLast && (isFetchingResponse || !isEmpty(organizedRealTimeUpdates.pending))) {
    return {
      agentEvents: organizedRealTimeUpdates.pending,
      // Once the exchange has failed the pending agent events are no longer being produced, so the
      // timeline must not keep rendering as actively generating above the error/retry state.
      isGenerating: !isFailed,
    };
  }

  return {
    agentEvents: organizedRealTimeUpdates.byExchangeId[exchangeId] || [],
    isGenerating: false,
  };
};

// The server sends an ISO string; a Date comes from the client (`toApiExchange`, tests), and
// `toISOString` throws on an invalid one.
const toIsoString = (createdAt: Date | string | null | undefined): string | null => {
  if (isNil(createdAt)) return null;
  if (typeof createdAt === 'string') return createdAt;
  return Number.isNaN(createdAt.getTime()) ? null : createdAt.toISOString();
};

/**
 * A claim exchange as the chat components read it. Its failure is not on the row: the claim chat only
 * knows the turn it is answering failed, which the controller reports as `activeExchange`.
 */
export const toChatExchangeView = (exchange: AiChatExchangeApiResponse): ChatExchangeView => ({
  id: String(exchange.id),
  userInput: exchange.user_input,
  status: exchange.response || exchange.response_time ? 'completed' : 'processing',
  response: exchange.response ?? null,
  error: null,
  createdAt: toIsoString(exchange.created_at),
  responseTimeMs: exchange.response_time ?? null,
});

// useChat replaces only the exchanges it changes, so a turn left untouched keeps its view, and with it
// the memoized message rows skip re-rendering when another turn is answered.
const VIEWS_BY_EXCHANGE = new WeakMap<AiChatExchangeApiResponse, ChatExchangeView>();

const viewOf = (exchange: AiChatExchangeApiResponse): ChatExchangeView => {
  const cached = VIEWS_BY_EXCHANGE.get(exchange);
  if (cached) return cached;
  const view = toChatExchangeView(exchange);
  VIEWS_BY_EXCHANGE.set(exchange, view);
  return view;
};

// Exchange ids are opaque strings at runtime, though `UseAiChatReturn` types them as numbers: cast
// rather than Number(), which would turn a non-numeric id into NaN.
const toClaimExchangeId = (exchangeId: string) => exchangeId as unknown as number;

const untitledClaimChatTitle = (chatId: string) => `Chat #${chatId}`;
// A claim chat's history id is its numeric internal id, which Mixpanel has always received as a number.
const claimChatAnalyticsId = (chatId: string) => Number(chatId);

/** The claim chat (`useChatContext()`) as a `ChatController`. Only for a claim page. */
export const useClaimChatController = (): ChatController => {
  const {
    chat,
    isChatError,
    exchanges,
    activeAsyncExchange,
    isFetchingResponse,
    getChatResponse,
    retryExchange,
    regenerateResponse,
    updateExchangeUserFeedback,
    restartChat,
    renameChat,
    reloadChat,
    loadChatById,
    references,
    chatDocumentIds,
    setChatDocumentIds,
    predefinedPrompts,
    organizedRealTimeUpdates,
    realTimeConnected,
  } = useChatContext();
  const { claim } = useClaim({ claimRequired: true });
  const source = useChatSource();

  const selectedDocumentIds = chatDocumentIds ?? NO_DOCUMENT_IDS;
  const chatInternalId = chat?.internal_id ?? null;
  const isAsyncFailed = activeAsyncExchange?.status === ASYNC_EXCHANGE_STATUS.FAILED;

  const exchangeViews = React.useMemo(() => exchanges.map(viewOf), [exchanges]);
  const exchangesById = React.useMemo(
    () => new Map(exchanges.map((exchange) => [String(exchange.id), exchange])),
    [exchanges]
  );

  const activeExchange = React.useMemo<ChatActiveExchange | null>(
    () =>
      activeAsyncExchange
        ? {
            id: activeAsyncExchange.exchangeId,
            status: activeAsyncExchange.status,
            error: activeAsyncExchange.error ?? null,
          }
        : null,
    [activeAsyncExchange]
  );

  const send = React.useCallback(
    (userInput: string) => getChatResponse(userInput, selectedDocumentIds),
    [getChatResponse, selectedDocumentIds]
  );
  // The claim chat retries the turn it was answering, whichever row shows the failure.
  const retry = React.useCallback(() => retryExchange(), [retryExchange]);
  const regenerate = React.useCallback(
    (exchangeId: string) => regenerateResponse(toClaimExchangeId(exchangeId)),
    [regenerateResponse]
  );
  const sendFeedback = React.useCallback(
    (exchangeId: string, score: number, text: string) =>
      updateExchangeUserFeedback(toClaimExchangeId(exchangeId), score, text),
    [updateExchangeUserFeedback]
  );
  const feedbackScoreFor = React.useCallback(
    (exchangeId: string) => exchangesById.get(exchangeId)?.user_feedback_score,
    [exchangesById]
  );

  const realtime = React.useMemo<ChatRealtime>(
    () => ({
      isConnected: realTimeConnected ?? false,
      progressFor: (exchangeId, isLast) => {
        const { agentEvents, isGenerating } = resolveAgentEvents(
          isLast,
          exchangeId,
          organizedRealTimeUpdates,
          isFetchingResponse,
          isLast && isAsyncFailed
        );
        return { events: agentEvents, isGenerating };
      },
    }),
    [realTimeConnected, organizedRealTimeUpdates, isFetchingResponse, isAsyncFailed]
  );

  const claimFeatures = React.useMemo<ClaimChatFeatures>(
    () => ({
      documents: claim.documents ?? NO_DOCUMENTS,
      selectedDocumentIds,
      setSelectedDocumentIds: setChatDocumentIds ?? noop,
      contextDocumentIdsFor: (exchangeId) => exchangesById.get(exchangeId)?.context_document_ids ?? NO_DOCUMENT_IDS,
      referenceIdsFor: (exchangeId) => exchangesById.get(exchangeId)?.documents_used ?? NO_REFERENCE_IDS,
      references: references ?? NO_REFERENCES,
      predefinedPrompts: predefinedPrompts ?? NO_PROMPTS,
      sendPrompt: (promptKey) => getChatResponse('', selectedDocumentIds, promptKey),
    }),
    [
      claim.documents,
      selectedDocumentIds,
      setChatDocumentIds,
      exchangesById,
      references,
      predefinedPrompts,
      getChatResponse,
    ]
  );

  const analytics = React.useMemo(() => ({ source, conversationId: chatInternalId }), [source, chatInternalId]);

  const listHistory = React.useCallback(async () => {
    const { data } = await axios.get<ClaimChatHistoryResponse>(`/api/v1/claims/${claim.id}/chat/history`);
    return data.chats.map((item) => ({
      id: String(item.id),
      title: item.chat_title ?? null,
      createdAt: item.created_at,
    }));
  }, [claim.id]);
  const openHistoryChat = React.useCallback(
    async (chatId: string) => {
      await loadChatById(Number(chatId));
    },
    [loadChatById]
  );
  const renameHistoryChat = React.useCallback(
    async (chatId: string, title: string) => {
      await axios.patch(`/api/v1/claims/${claim.id}/chat/${chatId}/rename`, { title });
      await reloadChat();
    },
    [claim.id, reloadChat]
  );
  const history = React.useMemo<ChatHistory>(
    () => ({
      currentId: isNil(chatInternalId) ? null : String(chatInternalId),
      list: listHistory,
      open: openHistoryChat,
      rename: renameHistoryChat,
      untitledTitle: untitledClaimChatTitle,
      analyticsIdOf: claimChatAnalyticsId,
    }),
    [chatInternalId, listHistory, openHistoryChat, renameHistoryChat]
  );

  return React.useMemo<ChatController>(
    () => ({
      conversationId: chat?.id ?? null,
      title: chat?.chat_title ?? null,
      loadState: chat ? 'ready' : isChatError ? 'error' : 'loading',
      exchanges: exchangeViews,
      activeExchange,
      isAnswering: isFetchingResponse,
      showWelcome: !!chat?.metadata?.is_auto_created && isEmpty(exchanges),
      send,
      retry,
      presentation: CLAIM_CHAT_PRESENTATION,
      analytics,
      realtime,
      regenerate,
      sendFeedback,
      feedbackScoreFor,
      startNewConversation: restartChat,
      rename: renameChat,
      history,
      claim: claimFeatures,
    }),
    [
      chat,
      isChatError,
      exchangeViews,
      activeExchange,
      isFetchingResponse,
      exchanges,
      send,
      retry,
      analytics,
      realtime,
      regenerate,
      sendFeedback,
      feedbackScoreFor,
      restartChat,
      renameChat,
      history,
      claimFeatures,
    ]
  );
};

export const ClaimChatControllerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const controller = useClaimChatController();
  return <ChatControllerProvider controller={controller}>{children}</ChatControllerProvider>;
};
